import {glsl} from './webgl';
import {fireEvent} from './utils';

export default {
    cube: {
        vertex: glsl`
            in vec2 aCoord;
            out vec2 vBackgroundCoord;
            out vec3 vCubeDirection;
            uniform mat4 uMatViewProjInv;
            uniform mat4 uMatBackground;
            void main() {
                vBackgroundCoord = (uMatBackground * vec4(aCoord, 0.0, 1.0)).xy;
                vec4 P = vec4(2.*(aCoord-.5), 0., 1.);
                gl_Position = P;
                P.z = 1.0;
                vCubeDirection = (uMatViewProjInv * P).xyz;
            }
        `,
        fragment: glsl`
            in vec2 vBackgroundCoord;
            in vec3 vCubeDirection;
            out vec4 fColor;
            uniform sampler2D uTexBackground;
            uniform samplerCube uTexCubeDoodle;
            uniform samplerCube uTexCubeUser;
            uniform sampler2D uTexGrain;
            uniform float uOpacity;

            vec3 applyGrain(vec3 C) {
                // construct grain uv from view direction
                vec3 d = abs(vCubeDirection);
                vec2 uv = vec2(d.x/d.z, d.y/d.z);
                // apply grain
                float grain = texture(uTexGrain, uv).g;
                C = mix(C, grain * C, 0.2);                 // darken
                C += 0.65 * vec3(grain * grain * grain);    // speckle
                return C;
            }

            // blend A over B
            vec4 blendPremultiplied(vec4 A, vec4 B) {
                float m = 1.0 - A.a;
                return vec4(
                    m * B.rgb + A.rgb,
                    m * B.a + A.a);
            }

            void main() {
                vec3 dir = normalize(vCubeDirection);
                vec4 foreground = blendPremultiplied(
                    texture(uTexCubeUser, dir),
                    texture(uTexCubeDoodle, dir));
                foreground.rgb = mix(foreground.rgb, applyGrain(foreground.rgb), foreground.a);
                vec4 background = texture(uTexBackground, vBackgroundCoord);
                fColor = blendPremultiplied(uOpacity * foreground, background);
            }
        `,
    },

    paint: {
        vertex: glsl`
            in vec2 aPosition;
            in vec2 aCoord;
            out vec2 vCoord;
            void main() {
                vCoord = aCoord;
                vec4 P = vec4(2.*(aPosition-.5), 0., 1.);
                gl_Position = P;
            }
        `,
        fragment: glsl`
            in vec2 vCoord;
            out vec4 fColor;
            uniform vec2 uDrawCoord;
            uniform sampler2D uTexStamp;
            uniform vec4 uColor;
            void main() {
                vec4 S = texture(uTexStamp, vCoord, 0.0);
                float mask = S.g;
                float alpha = S.g * uColor.a;
                fColor = vec4(alpha * uColor.rgb, alpha);
            }
        `,
    },

    circle: {
        vertex: glsl`
            in vec2 aCoord;
            out vec2 vCoord;
            uniform vec4 uCircle;
            void main() {
                vCoord = aCoord;

                // circle parameters
                vec2 scale = uCircle.zw;
                vec2 center = uCircle.xy;

                // this is in 0..1 coords
                vec2 P = center + scale * 2.0 * (aCoord - 0.5);

                gl_Position = vec4(
                    2.0 * (P - 0.5),
                    0.0,
                    1.0);
            }
        `,
        fragment: glsl`
            in vec2 vCoord;
            out vec4 fColor;
            void main() {
                float d = 2.25 * distance(vCoord, vec2(0.5));
                //float w = fwidth(d);
                float alpha = smoothstep(
                    0.2,
                    0.0,
                    abs(d - 1.0));
                fColor = vec4(alpha);
            }
        `,
    },
};

if (import.meta.hot) {
    import.meta.hot.accept(({default: sources}) =>
        fireEvent(document, 'sde:shaders:hot', sources));
}
